import { useState } from "react"
import styled from "styled-components"

import { Text } from "@minuthq/meatball-ui-react"
import { subWeeks } from "date-fns"
import { fromZonedTime } from "date-fns-tz"

import { IGraphDateRange } from "src/components/Homes/DeviceDetails/Overview/DeviceGraphs"
import { AiAssistantAnalyticsSummaryCard } from "src/components/Reports/AiAssistantAnalytics/AiAssistantAnalyticsSummaryCard"
import { AiAssistantPerformanceBarChart } from "src/components/Reports/AiAssistantAnalytics/AiAssistantPerformanceBarChart"
import { useAiAssistantAnalyticsParams } from "src/components/Reports/AiAssistantAnalytics/useAiAssistantAnalyticsParams"
import { TimezoneSelect } from "src/components/Reports/TimezoneSelect"
import { TTimezone } from "src/data/timezones/timezones"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { IBreadcrumb } from "src/ui/Breadcrumbs/Breadcrumbs"
import { DateRangePicker } from "src/ui/DateRangePicker/DateRangePicker"
import { BreadcrumbView } from "src/ui/Layout/BreadcrumbView"
import { spacing } from "src/ui/spacing"
import { getStoredSelectedTimezone } from "src/utils/l10n"

export function AiAssistantAnalytics() {
  const { t, langKeys, tPlaceholder } = useTranslate()

  const [timezone, setTimezone] = useState<TTimezone>(
    getStoredSelectedTimezone().timezone
  )

  const { dateRange, setDateRange } = useAiAssistantAnalyticsParams()
  const startDate = fromZonedTime(dateRange.startDate, timezone.tzCode)
  const endDate = fromZonedTime(dateRange.endDate, timezone.tzCode)

  const breadcrumbs: IBreadcrumb[] = [
    { label: t(langKeys.report_plural), to: Routes.Reports.location() },
    {
      label: tPlaceholder("AI Assistant Analytics"),
      to: Routes.AiAssistantAnalytics.location(),
    },
  ]

  return (
    <BreadcrumbView
      breadcrumbs={breadcrumbs}
      title={
        <Text variant="heading1">{tPlaceholder("AI Assistant Analytics")}</Text>
      }
      actionBar={
        <ActionBar dateRange={dateRange} setDateRange={setDateRange} />
      }
    >
      <GridContainer>
        <AiAssistantAnalyticsSummaryCard
          startDate={startDate.toISOString()}
          endDate={endDate.toISOString()}
        />

        <AiAssistantPerformanceBarChart
          startDate={startDate.toISOString()}
          endDate={endDate.toISOString()}
          timezone={timezone}
        />

        <TimezoneContent>
          <Text variant="body">{t(langKeys.timezone)}: </Text>
          <TimezoneSelect timezone={timezone} onChange={setTimezone} />
        </TimezoneContent>
      </GridContainer>
    </BreadcrumbView>
  )
}

function ActionBar({
  dateRange,
  setDateRange,
}: {
  dateRange: IGraphDateRange
  setDateRange: (dateRange: IGraphDateRange) => void
}) {
  return (
    <DateRangePicker
      startDate={dateRange.startDate}
      endDate={dateRange.endDate}
      onDateRangeChange={setDateRange}
      dateRangeProps={{
        minBookingDays: 1,
      }}
      onSelectPreset={({ clearSelection }) => {
        if (clearSelection) {
          setDateRange({
            startDate: subWeeks(new Date(), 5),
            endDate: new Date(),
          })
        }
      }}
      defaultPresetKey="DAY"
      calendarPlacement="top-end"
      hidePresets={["DAY", "WEEK", "YEAR"]}
      disabled={false}
    />
  )
}

const GridContainer = styled.div`
  display: grid;
  gap: ${spacing.L};
`

const TimezoneContent = styled.div`
  display: flex;
  gap: ${spacing.S};
  align-items: center;
`
